import { AtSign, Bell, FileEdit, MessageCircleReply, UserCheck } from "lucide-react";
import type { ElementType } from "react";

// ============================================================================
// Notification Helpers
// ============================================================================
// Utility functions for rendering notification UI elements.

/**
 * Map notification type to a Lucide icon component.
 */
export function getNotificationIcon(type: string): ElementType {
  switch (type) {
    case "clarification_response":
      return MessageCircleReply;
    case "clarification_assigned":
    case "section_owner_assignment":
      return UserCheck;
    case "entity_change":
      return FileEdit;
    case "mention":
      return AtSign;
    default:
      return Bell;
  }
}

/**
 * Map notification type to a color class for the icon.
 */
export function getNotificationIconColor(type: string): string {
  switch (type) {
    case "clarification_response":
      return "text-blue-500";
    case "clarification_assigned":
    case "section_owner_assignment":
      return "text-green-500";
    case "entity_change":
      return "text-orange-500";
    case "mention":
      return "text-purple-500";
    default:
      return "text-muted-foreground";
  }
}

/**
 * Format a timestamp into a relative time string.
 *
 * Not `formatSmartDate` from `@/lib/formatters`, which turns absolute after a day: a
 * notification list is read as "how long have I not looked at this", so the week of
 * day-granular buckets below is the point of it.
 *
 * Uses simple thresholds instead of a library:
 * - <1m: "just now"
 * - <60m: "Xm ago"
 * - <24h: "Xh ago"
 * - <7d: "Xd ago"
 * - otherwise: date string
 */
export function formatTimeAgo(timestamp: number): string {
  const now = Date.now();
  const diff = now - timestamp;
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;

  return new Date(timestamp).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}
